import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { UseCases } from "@planzone/core";
import {
  useAuthentication,
  useSaveTimeRecord,
  useRetrieveCurrentPlanzone,
  useRetrievePlanzoneSettings,
  useRetrieveSecondaryCriteriaTemplate,
} from "@planzone/view-models";
import {
  usePlanzonePermissions,
  useProjectPermissions,
} from "@components/permissions";
import { useModal } from "@root/client/modal";
import { Props } from "./types";
import { NumberInput } from "./numberInput";
import { parseTimestamp, toFixed } from "@pages/sharedTimeSheetUtils";
import styles from "./styles.scss";

function Record({
  date,
  record: _record,
  timeSpent: time,
  resource,
  task,
  project,
  type,
  isReloading,
  timesheetDeadline,
  attributes,
}: Props) {
  const [timeSpent, setSpentTime] = React.useState(
    _record ? _record.timeSpent : time || 0,
  );
  const record = _record
    ? { ..._record, timeSpent }
    : {
        resource,
        task,
        timeSpent,
        date: date.day,
        month: date.month,
        year: date.year,
      };
  const { isRequestPending, saveTimeRecord } = useSaveTimeRecord(record);
  const { planzone } = useRetrieveCurrentPlanzone();
  const { settings } = useRetrievePlanzoneSettings(planzone);
  const { template } = useRetrieveSecondaryCriteriaTemplate(
    planzone.uuid,
    "timeRecord",
  );
  const { t } = useTranslation();
  const { pushView } = useModal();
  const { account } = useAuthentication();
  const { isAdministrator } = usePlanzonePermissions();
  const { isProjectManager } = useProjectPermissions(project);

  const isLocked = (() => {
    if (timesheetDeadline != null && date <= parseTimestamp(timesheetDeadline))
      return true;
    if (
      settings.lockBeforeIntegrationDate &&
      resource.integratedAt != null &&
      date <= parseTimestamp(resource.integratedAt)
    )
      return true;
    return false;
  })();

  const canEdit =
    !isLocked &&
    (isAdministrator ||
      (resource.uuid === account.resource.uuid &&
        settings.canEditPersonalTimeSheet) ||
      (isProjectManager && settings.canEditProjectPersonalTimeSheet) ||
      settings.canEditManagerPersonalTimeSheet);

  const hasCriteria = type === "task" && template.length > 0;
  const canOpenCriteria = canEdit || record.timeSpent > 0;
  const openCriteria = () =>
    pushView({
      key: UseCases.saveTimeRecord,
      data: {
        timeRecord: {
          ...record,
          attributes: attributes ?? _record?.attributes,
        },
        isDisabled: !canEdit,
      },
    });

  React.useEffect(() => {
    !_record && setSpentTime(time);
  }, [time]);

  return (
    <div
      className={classNames(styles.record, {
        [styles.resource]: type === "resource",
        [styles.list]: type === "list" || type === "project",
        [styles.task]: type === "task",
        [styles.locked]: isLocked,
      })}
    >
      {type === "task" && hasCriteria ? (
        <p
          className={classNames(styles.input, {
            [styles.clickable]: canOpenCriteria,
          })}
          title={t("timeRecordCriteria.open")}
          onClick={() => canOpenCriteria && openCriteria()}
        >
          {toFixed(record.timeSpent)}
        </p>
      ) : type === "task" && canEdit ? (
        <NumberInput
          nbr={record.timeSpent}
          isDisabled={isRequestPending}
          edit={(nbr) => setSpentTime(nbr < 0 ? 0 : nbr)}
          submit={(timeSpent) =>
            !isRequestPending &&
            !isLocked &&
            saveTimeRecord({
              timeRecord: { ...record, timeSpent },
            })
          }
        />
      ) : (
        <p className={styles.input}>{toFixed(record.timeSpent)}</p>
      )}
    </div>
  );
}

export { Record };
